import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Wifi,
  SignalCellular4Bar,
  SignalCellular0Bar,
  SignalCellular1Bar,
  SignalCellular2Bar,
  SignalCellular3Bar,
  WbSunny,
  Cloud,
  Opacity,
  Air,
  LocationOn,
  Settings,
  Refresh,
  Thermostat,
  Visibility,
  Speed,
} from '@mui/icons-material';
import axiosInstance from '../axiosConfig';
import moment from 'moment';
import { useLanguage } from '../../contexts/LanguageContext';

// Import existing widgets
import RoomSensorsWidget from './RoomSensorsWidget';
import EmployeesWidget from './EmployeesWidget';
import AccessLogsWidget from './AccessLogsWidget';

// Unifi Widget Component
const UnifiWidget = ({ projectId }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [openRoomDialog, setOpenRoomDialog] = useState(false);
  const { translate } = useLanguage();

  useEffect(() => {
    fetchRoomData();
  }, [projectId]);

  const fetchRoomData = async () => {
    try {
      setLoading(true);
      // Simulate API call for room data
      const mockRooms = [
        {
          id: 1,
          name: translate('conferenceRoomA'),
          status: 'online',
          signal: 85,
          clients: 12,
          temperature: 22,
          humidity: 45,
          lastSeen: new Date(),
        },
        {
          id: 2,
          name: translate('mainOffice'),
          status: 'online',
          signal: 92,
          clients: 8,
          temperature: 24,
          humidity: 48,
          lastSeen: new Date(),
        },
        {
          id: 3,
          name: translate('receptionArea'),
          status: 'offline',
          signal: 0,
          clients: 0,
          temperature: 21,
          humidity: 42,
          lastSeen: new Date(Date.now() - 3600000),
        },
        {
          id: 4,
          name: translate('breakRoom'),
          status: 'online',
          signal: 78,
          clients: 3,
          temperature: 23,
          humidity: 50,
          lastSeen: new Date(),
        },
      ];
      setRooms(mockRooms);
    } catch (error) {
      console.error('Error fetching room data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSignalIcon = (signal) => {
    if (signal === 0) return <SignalCellular0Bar color="error" />;
    if (signal < 25) return <SignalCellular1Bar color="warning" />;
    if (signal < 50) return <SignalCellular2Bar color="warning" />;
    if (signal < 75) return <SignalCellular3Bar color="success" />;
    return <SignalCellular4Bar color="success" />;
  };

  const getStatusColor = (status) => {
    return status === 'online' ? 'success' : 'error';
  };

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
    setOpenRoomDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('roomInformation')}
          </Typography>
          <IconButton onClick={fetchRoomData} size="small">
            <Refresh />
          </IconButton>
        </Box>
        
        <Grid container spacing={2}>
          {rooms.map((room) => (
            <Grid item xs={12} sm={6} key={room.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                  border: `2px solid ${room.status === 'online' ? '#4caf50' : '#f44336'}`,
                }}
                onClick={() => handleRoomClick(room)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {room.name}
                    </Typography>
                    {getSignalIcon(room.signal)}
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Chip
                      label={translate(room.status)}
                      color={getStatusColor(room.status)}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {room.clients} {translate('clients')}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Thermostat fontSize="small" color="action" />
                      <Typography variant="caption">
                        {room.temperature}°C
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Opacity fontSize="small" color="action" />
                      <Typography variant="caption">
                        {room.humidity}%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Room Details Dialog */}
      <Dialog
        open={openRoomDialog}
        onClose={() => setOpenRoomDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 600,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          pb: 2
        }}>
          {translate('detailsOf')} {selectedRoom?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRoom && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('status')}
                  </Typography>
                  <Chip
                    label={translate(selectedRoom.status)}
                    color={getStatusColor(selectedRoom.status)}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('wifiSignal')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getSignalIcon(selectedRoom.signal)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {selectedRoom.signal}%
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('connectedClients')}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {selectedRoom.clients}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('lastActivity')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {moment(selectedRoom.lastSeen).fromNow()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('temperature')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Thermostat color="action" />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {selectedRoom.temperature}°C
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('humidity')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Opacity color="action" />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {selectedRoom.humidity}%
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setOpenRoomDialog(false)}
            variant="outlined"
          >
            {translate('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Weather Widget Component
const WeatherWidget = ({ projectId }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('Los Angeles');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [openCityDialog, setOpenCityDialog] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { translate } = useLanguage();
  
  const [availableCities] = useState([
    { name: 'Los Angeles', timezone: 'America/Los_Angeles' },
    { name: 'New York', timezone: 'America/New_York' },
    { name: 'Chicago', timezone: 'America/Chicago' },
    { name: 'Denver', timezone: 'America/Denver' },
    { name: 'Phoenix', timezone: 'America/Phoenix' },
    { name: 'Miami', timezone: 'America/New_York' },
    { name: 'Seattle', timezone: 'America/Los_Angeles' },
    { name: 'San Francisco', timezone: 'America/Los_Angeles' },
  ]);

  useEffect(() => {
    fetchWeatherData();
    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, [city, timezone]);

  const fetchWeatherData = async () => {
    try {
      setLoading(true);
      // Simulate API call for weather data with US-based conditions
      const weatherConditions = [translate('sunny'), translate('partlyCloudy'), translate('cloudy'), translate('rainy'), translate('clear')];
      const mockWeather = {
        city: city,
        timezone: timezone,
        temperature: Math.floor(Math.random() * 30) + 10,
        condition: weatherConditions[Math.floor(Math.random() * weatherConditions.length)],
        humidity: Math.floor(Math.random() * 40) + 30,
        windSpeed: Math.floor(Math.random() * 20) + 5,
        visibility: Math.floor(Math.random() * 10) + 5,
        pressure: Math.floor(Math.random() * 20) + 1000,
        feelsLike: Math.floor(Math.random() * 30) + 10,
        icon: 'wb_sunny',
        sunrise: '06:30',
        sunset: '19:45',
        uvIndex: Math.floor(Math.random() * 10) + 1,
      };
      
      setTimeout(() => {
        setWeather(mockWeather);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setLoading(false);
    }
  };

  const getWeatherIcon = (condition) => {
    switch (condition.toLowerCase()) {
      case 'sunny':
      case 'clear':
        return <WbSunny color="warning" />;
      case 'partly cloudy':
      case 'cloudy':
        return <Cloud color="action" />;
      case 'rainy':
        return <Opacity color="primary" />;
      default:
        return <WbSunny color="warning" />;
    }
  };

  const formatTime = (date, timezone) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (error) {
      return date.toLocaleTimeString('en-US', { hour12: true });
    }
  };

  const formatDate = (date, timezone) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('weatherAndTime')}
          </Typography>
          <IconButton onClick={() => setOpenCityDialog(true)} size="small">
            <LocationOn />
          </IconButton>
        </Box>

        {weather && (
          <Box>
            {/* Current Time and Date */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(102, 126, 234, 0.1)', borderRadius: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 0.5 }}>
                {formatTime(currentTime, weather.timezone)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {formatDate(currentTime, weather.timezone)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                {weather.city} • {weather.timezone.replace('_', ' ')}
              </Typography>
            </Box>

            {/* Weather Information */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                {getWeatherIcon(weather.condition)}
                <Typography variant="h4" sx={{ ml: 2, fontWeight: 700 }}>
                  {weather.temperature}°C
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {weather.city}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {weather.condition}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Opacity color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate('humidity')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {weather.humidity}%
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Air color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate('wind')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {weather.windSpeed} km/h
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Visibility color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate('visibility')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {weather.visibility} km
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Speed color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate('pressure')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {weather.pressure} hPa
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Typography variant="caption" color="text.secondary">
                {translate('feelsLike')}: {weather.feelsLike}°C • UV: {weather.uvIndex}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* City Selection Dialog */}
      <Dialog
        open={openCityDialog}
        onClose={() => setOpenCityDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 600,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          pb: 2
        }}>
          {translate('selectCityTimezone')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{translate('city')}</InputLabel>
            <Select
              value={city}
              onChange={(e) => {
                const selectedCity = availableCities.find(c => c.name === e.target.value);
                setCity(e.target.value);
                setTimezone(selectedCity.timezone);
              }}
              label={translate('city')}
            >
              {availableCities.map((cityOption) => (
                <MenuItem key={cityOption.name} value={cityOption.name}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>{cityOption.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cityOption.timezone.replace('_', ' ')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ p: 2, bgcolor: 'rgba(102, 126, 234, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {translate('currentLocalTime')}:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatTime(currentTime, timezone)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(currentTime, timezone)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setOpenCityDialog(false)}
            variant="outlined"
          >
            {translate('cancel')}
          </Button>
          <Button 
            onClick={() => {
              fetchWeatherData();
              setOpenCityDialog(false);
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {translate('update')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Analytics Widget Component
const AnalyticsWidget = ({ projectId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { translate } = useLanguage();

  useEffect(() => {
    fetchAnalytics();
  }, [projectId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      // Simulate API call for analytics data
      const mockAnalytics = {
        totalEmployees: 45,
        activeEmployees: 38,
        totalRooms: 12,
        occupiedRooms: 8,
        energyConsumption: 75,
        securityAlerts: 2,
        maintenanceRequests: 5,
        visitorCount: 23,
      };
      
      setTimeout(() => {
        setAnalytics(mockAnalytics);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {translate('generalAnalysis')}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
              {analytics?.totalEmployees}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('totalEmployees')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2 }}>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
              {analytics?.activeEmployees}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('activeEmployees')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
            <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
              {analytics?.totalRooms}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('rooms')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 2 }}>
            <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 700 }}>
              {analytics?.occupiedRooms}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('occupied')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {translate('energyConsumption')}
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={analytics?.energyConsumption} 
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary">
          {analytics?.energyConsumption}% {translate('ofDailyConsumption')}
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {translate('securityAlerts')}
        </Typography>
        <Chip 
          label={`${analytics?.securityAlerts} ${translate('activeAlerts')}`}
          color={analytics?.securityAlerts > 0 ? 'warning' : 'success'}
          size="small"
        />
      </Box>
    </Box>
  );
};

// Notifications Widget Component
const NotificationsWidget = ({ projectId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { translate } = useLanguage();

  useEffect(() => {
    fetchNotifications();
  }, [projectId]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Simulate API call for notifications
      const mockNotifications = [
        {
          id: 1,
          type: 'security',
          message: translate('unauthorizedAccessDetected'),
          timestamp: new Date(Date.now() - 300000),
          priority: 'high',
        },
        {
          id: 2,
          type: 'maintenance',
          message: translate('scheduledMaintenance'),
          timestamp: new Date(Date.now() - 1800000),
          priority: 'medium',
        },
        {
          id: 3,
          type: 'system',
          message: translate('systemUpdateCompleted'),
          timestamp: new Date(Date.now() - 3600000),
          priority: 'low',
        },
        {
          id: 4,
          type: 'energy',
          message: translate('energyConsumptionAbove'),
          timestamp: new Date(Date.now() - 7200000),
          priority: 'medium',
        },
      ];
      
      setTimeout(() => {
        setNotifications(mockNotifications);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'security':
        return <Settings color="error" />;
      case 'maintenance':
        return <Settings color="warning" />;
      case 'system':
        return <Settings color="success" />;
      case 'energy':
        return <Settings color="info" />;
      default:
        return <Settings color="action" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {translate('notifications')}
      </Typography>
      
      <List sx={{ p: 0 }}>
        {notifications.map((notification) => (
          <ListItem
            key={notification.id}
            sx={{
              border: `1px solid rgba(0,0,0,0.1)`,
              borderRadius: 2,
              mb: 1,
              '&:last-child': { mb: 0 },
            }}
          >
            <ListItemIcon>
              {getNotificationIcon(notification.type)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {notification.message}
                  </Typography>
                  <Chip
                    label={translate(notification.priority)}
                    color={getPriorityColor(notification.priority)}
                    size="small"
                  />
                </Box>
              }
              secondary={moment(notification.timestamp).fromNow()}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

// Settings Widget Component
const SettingsWidget = ({ projectId }) => {
  const [settings, setSettings] = useState({
    notifications: true,
    autoRefresh: true,
    darkMode: false,
    language: 'en',
    timezone: 'America/Los_Angeles',
  });
  const { translate } = useLanguage();

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {translate('settings')}
      </Typography>
      
      <List sx={{ p: 0 }}>
        <ListItem>
          <ListItemText
            primary={translate('notifications')}
            secondary={translate('receiveRealTimeAlerts')}
          />
          <Chip
            label={settings.notifications ? translate('enabled') : translate('disabled')}
            color={settings.notifications ? 'success' : 'default'}
            size="small"
            onClick={() => handleSettingChange('notifications', !settings.notifications)}
            sx={{ cursor: 'pointer' }}
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary={translate('autoRefresh')}
            secondary={translate('updateDataAutomatically')}
          />
          <Chip
            label={settings.autoRefresh ? translate('enabled') : translate('disabled')}
            color={settings.autoRefresh ? 'success' : 'default'}
            size="small"
            onClick={() => handleSettingChange('autoRefresh', !settings.autoRefresh)}
            sx={{ cursor: 'pointer' }}
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary={translate('darkMode')}
            secondary={translate('changeApplicationTheme')}
          />
          <Chip
            label={settings.darkMode ? translate('enabled') : translate('disabled')}
            color={settings.darkMode ? 'primary' : 'default'}
            size="small"
            onClick={() => handleSettingChange('darkMode', !settings.darkMode)}
            sx={{ cursor: 'pointer' }}
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary={translate('language')}
            secondary="English"
          />
          <Chip
            label="EN"
            color="primary"
            size="small"
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary={translate('timezone')}
            secondary="America/Los_Angeles"
          />
          <Chip
            label="PST"
            color="info"
            size="small"
          />
        </ListItem>
      </List>
    </Box>
  );
};

const WidgetFactory = ({ widget, projectId, onSettings, onRemove }) => {
  const { translate } = useLanguage();
  
  const renderWidget = () => {
    switch (widget.type) {
      case 'room-sensors':
        return <RoomSensorsWidget projectId={projectId} />;
      case 'employees':
        return <EmployeesWidget projectId={projectId} />;
      case 'access-logs':
        return <AccessLogsWidget projectId={projectId} />;
      case 'unifi':
        return <UnifiWidget projectId={projectId} />;
      case 'weather':
        return <WeatherWidget projectId={projectId} />;
      case 'analytics':
        return <AnalyticsWidget projectId={projectId} />;
      case 'notifications':
        return <NotificationsWidget projectId={projectId} />;
      case 'settings':
        return <SettingsWidget projectId={projectId} />;
      default:
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {translate('widgetInDevelopment', { type: widget.type })}
            </Typography>
          </Box>
        );
    }
  };

  return renderWidget();
};

export default WidgetFactory; 